import type { TypographyProps } from '@mui/material';
import { Box, Skeleton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import type { Option, Sorting } from '@/shared/types';
import { SortTypes } from '@/shared/types';
import type { ReducedSortingItem } from '@/shared/ui';
import { AdvancedSortButton, SortButton, getColumnSorting } from '@/shared/ui';
import { ColumnItem } from '../ColumnItem/ColumnItem';
import { DraggableList } from '../DraggableList/DraggableList';

const Header = styled((props: TypographyProps) => {
  return <Typography {...props} />;
})(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  paddingBottom: theme.spacing(1),
  fontWeight: 600,
  fontSize: '0.875rem',
  lineHeight: '1rem',
  letterSpacing: 0,
  color: '#7E828F',
  backgroundColor: 'white',
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));

export interface ColumnInfo {
  id: string;
  name: string;
  description: string;
}

interface Props {
  loaded: boolean;
  title: string;
  columns: ColumnInfo[];
  onDelete: (id: string) => void;
  onChange: (columns: ColumnInfo[]) => void;
  sorting?: Sorting;
  advancedSortableOptions?: Option[];
  onSortChange: (id: string, v?: ReducedSortingItem) => void;
}

export function ColumnsGroupList({
  loaded,
  title,
  columns,
  onDelete,
  onChange,
  sorting,
  advancedSortableOptions,
  onSortChange,
}: Readonly<Props>) {
  // Scope HTML5Backend to this container so its window-level drop listener doesn't
  // fire for native drags originating from MUI DataGrid columns on the same page.
  // Without this, handleTopDrop (react-dnd-html5-backend@16.0.1) calls actions.hover()
  // unconditionally on every drop event, throwing "Cannot call hover while not dragging".
  const [root, setRoot] = useState<HTMLElement | null>(null);

  if (!loaded) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Header>{title}</Header>
        <Skeleton
          sx={{ borderRadius: 2, height: 50, ml: 3 }}
          variant="rectangular"
        />
      </Box>
    );
  }

  return (
    <Box ref={setRoot}>
      <Header>{title}</Header>
      {columns.length === 0 && (
        <Typography sx={{ ml: 3 }} color="secondary" variant="caption">
          Nothing selected
        </Typography>
      )}
      {root != null && (
        <DndProvider backend={HTML5Backend} options={{ rootElement: root }}>
          <DraggableList
            sx={{ pl: 3, mb: 1 }}
            items={columns}
            renderItem={(item, dragHandleRef) => {
              const sortingItem = sorting
                ? getColumnSorting(sorting, item.id)
                : undefined;
              const advancedField =
                sortingItem?.type === SortTypes.ADVANCED
                  ? sortingItem.field
                  : undefined;
              const sortSlot = advancedSortableOptions?.length ? (
                <AdvancedSortButton
                  advancedFieldOptions={advancedSortableOptions}
                  order={sortingItem?.index}
                  advancedField={advancedField}
                  value={sortingItem?.value}
                  onChange={(v) => onSortChange(item.id, v)}
                />
              ) : (
                <SortButton
                  order={sortingItem?.index}
                  value={sortingItem?.value}
                  onChange={(v) =>
                    onSortChange(
                      item.id,
                      v ? { type: SortTypes.SIMPLE, value: v } : undefined,
                    )
                  }
                />
              );
              return (
                <ColumnItem
                  {...item}
                  onDelete={onDelete}
                  dragHandleRef={dragHandleRef}
                  sortSlot={sortSlot}
                />
              );
            }}
            onChange={onChange}
          />
        </DndProvider>
      )}
    </Box>
  );
}
